/**
 * @module components/Chart/ChartUHS
 * @author Charles Blais <charles.blais@canada.ca>
 */

import { useMemo, useState } from 'react';
import ReactEcharts from 'echarts-for-react';

import { useLanguageState } from '../../contexts/language-state-provider';
import { useAppState } from '../../contexts/app-state-provider';

import {
  getToolbox, getGrid, getLegend, getTooltip,
  getXAxis, getYAxis, getDataZoom, getOnEvents,
  saToSeries, getSubtext, modifyColorToSeries,
  markerIcon,
} from './lib';


type Props = {
  variables: HazardParameters,
  siteDesignations: SiteDesignation[],
  initialLog?: boolean,
  initialSelected?: string[],
}


/**
 * Convert the site designations object list into a list
 * that is echart friendly.
 * 
 * @param {*} siteDesignations
 * @param {*} colorTable
 */
const makeSeries = (
  siteDesignations: SiteDesignation[]
) => {
  return siteDesignations
  .sort((a, b) => (Number(a.poe50) > Number(b.poe50)?1:-1))
  .map((site) => {
    console.log(site.poe50);
    console.log(saToSeries(site));
    return {
      name: `${site.siteDesignation} ${site.poe50}%/50y`,
      type: 'line',
      symbol: markerIcon,
      symbolSize: 15,
      itemStyle: {
        borderWidth: 3,
        // borderColor added by modifyColorToSeries
        color: 'none',
        fill: 'none',
      },
      lineStyle: {
        type: 'dashed',
        // color added by modifyColorToSeries
        width: 3,
      },
      data: saToSeries(site),
      markPoint: {
        symbol: 'square',
        symbolSize: 5,
        itemStyle: {
          borderWidth: 3,
          // borderColor added by modifyColorToSeries
          color: 'none',
          fill: 'none',
        },
        data: [{
          name: 'fixed x position',
          yAxis: site.pga,
          xAxis: 0.01
        }],
        label: {
          show: false,
          formatter: 'PGA',
          position: 'end',
        }
      },
      markLine: {
        symbol:"none",             
        itemStyle:{
          normal:{
            label:{
              show:true,
              position:'end'
            }
          }
        },
        data: [{
          name: 'PGA',
          xAxis: 0.01, 
          label:{
            show:true,
            position:'start',
            formatter: 'PGA',
          }
        }],
     },
    }
  });
};


/**
 * React component
 * ===============
 */
export const ChartSA = ({
  variables,
  siteDesignations,
  initialLog = false,
  initialSelected = [
    '10%/50y',
    ' 5%/50y',
    '2%/50y',
  ],
}: Props) => {
  const [ lang ] = useLanguageState();
  const [ state ] = useAppState();

  const [ log, setLog ] = useState(initialLog);
  // save a state for legend selection
  const [ selected, setSelected ] = useState(initialSelected);

  const series = useMemo(() => modifyColorToSeries(
    makeSeries(siteDesignations), state.chart.colorTable
  ), [state, siteDesignations]);

  const subtext = getSubtext(variables, log, lang);

  return (
    <ReactEcharts
      style={{ height: 600 }}
      onEvents={getOnEvents(setSelected)}
      option={{
        title: {
          text: lang.chart.uhs,
          subtext,
          textStyle: {
            fontSize: 20,
          }
        },
        color: state.chart.colorTable,
        tooltip: getTooltip(),
        legend: getLegend(series, selected, false),
        grid: {
          ...getGrid(),
          // for some odd reaons, new echat v5 may have a bug
          // when it comes to left side padding with labels
          left: '5%',
        },
        toolbox: getToolbox(lang, log, setLog),
        xAxis: {
          ...getXAxis(lang.chart.period, log),
          min: 0.01
        },
        yAxis: getYAxis(lang.chart.gm, log),
        dataZoom: getDataZoom(),
        series,
      }}
    />
  )
};


export default ChartSA;
